import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Users, Stethoscope, MousePointerSquareDashed, X } from 'lucide-react'
import { shiftTypeTone, relabelBranch, isLeaveRow, TIMETABLE_TYPES } from '../../lib/clockin.js'
import { indexBranches, positionShort } from '../../lib/employee.js'
import {
  monthDays, DOW_TH, weekEdge, shortType, rosterPeople, groupPeople, personStats,
  dayStats, predictType, ineligibleFor,
} from '../../lib/employeeMatrix.js'
import { foreignRowOn } from '../../lib/crossBranch.js'
import EmployeeMonthDrawer from './EmployeeMonthDrawer.jsx'

// A column has to hold "ผช" and a two-digit date and still let a 31-day month
// sit on one screen next to a name column wide enough to read.
const COL = 'w-[34px] min-w-[34px]'
const NAME_COL = 'w-[218px] min-w-[218px]'
// Explicit, because the second pinned header row has to be told where the first
// one ends — `sticky top-0` twice would stack them on top of each other.
const H_DATES = 30
const H_DOCTORS = 22

const cellKey = (employeeId, date) => `${employeeId}|${date}`

/* What a person's month holds beyond plain shifts. Only non-zero counts are
 * drawn, so the usual row stays just a name and a number — in a 200-row sample
 * allow_OT ran 3% and special_OT none at all, which is exactly the frequency
 * that makes a badge worth reading when it does appear.
 *
 * COLOUR IS RESERVED. ก, ป and LWP are tinted because those tints are the same ones
 * their days wear out in the grid — the badge and the cell are the same fact.
 * Nothing in the grid is emerald or violet, so an OT badge in those colours was
 * inventing a code the rest of the screen does not speak. The two OT counts sit
 * on the background instead and are told apart by the ★, which needs no key. */
const FLAGS = [
  { key: 'personal', glyph: 'ก', label: 'ลากิจ', tone: 'bg-amber-50 text-amber-700' },
  { key: 'sick', glyph: 'ป', label: 'ลาป่วย', tone: 'bg-rose-50 text-rose-700' },
  { key: 'unpaid', glyph: 'LWP', label: 'Leave without pay', tone: 'bg-stone-200 text-stone-700' },
  { key: 'otAllow', glyph: 'OT', label: 'อนุญาต OT', tone: 'bg-slate-100 text-slate-600' },
  { key: 'otSpecial', glyph: 'OT★', label: 'OT พิเศษ', tone: 'bg-slate-100 text-slate-600' },
]

function Flags({ stats }) {
  const on = FLAGS.filter((f) => stats[f.key] > 0)
  if (!on.length) return null
  return (
    <span className="flex items-baseline gap-0.5 shrink-0"
      title={on.map((f) => `${f.label} ${stats[f.key]}`).join(' · ')}>
      {on.map((f) => (
        <span key={f.key} className={`rounded px-1 text-[9.5px] font-medium tabular-nums ${f.tone}`}>
          {f.glyph}{stats[f.key]}
        </span>
      ))}
    </span>
  )
}

/**
 * ตารางเวรพนักงาน แบบ คน × วัน — one branch-month as a matrix.
 *
 * The calendar answers "what is on the 12th"; this answers the two questions a
 * roster is actually built from, and answers them from the axes rather than
 * from a report bolted on the side:
 *
 *   · read a ROW across → one person's month (the number in the sticky name
 *     cell is their shift count, which is what makes a month fair);
 *   · read a COLUMN down → that day's team; every position band carries its own
 *     per-day count on its header row, so "ผู้ช่วย 4 · Counter 3" is read where
 *     those people are, not in a separate summary.
 *
 * Pinned while you scroll: the dates and the doctor count at the top, the names
 * on the left, the day total at the bottom. Rostering is comparing a cell with
 * its headers, and a header you have to scroll back to is not a header.
 *
 * Leave is drawn in the cell but excluded from every headcount — a ลากิจ row
 * means that person is NOT there, and a total that counts them is the one
 * number here that could get an understaffed day signed off.
 */
export default function EmployeeMatrix({
  ym, rows = [], employees = [], empById, branchId, branches = [],
  doctorsByDay, foreign, foreignLoading, onNeedForeign,
  onCreate, onEdit, onBulkAssign, busy, today,
  openPersonId, onOpenPerson,
}) {
  const days = useMemo(() => monthDays(ym.year, ym.month), [ym])
  const branchName = useMemo(() => indexBranches(branches), [branches])

  const people = useMemo(
    () => rosterPeople({ employees, rows, branchId, empById }),
    [employees, rows, branchId, empById],
  )
  const groups = useMemo(() => groupPeople(people), [people])
  const byDay = useMemo(() => dayStats(rows, empById), [rows, empById])
  const monthTotal = useMemo(() => [...byDay.values()].reduce((n, c) => n + c.total, 0), [byDay])

  /* ── multi-select ──────────────────────────────────────────────────────
   * No mode to enter: the gesture says what it means. A plain click still opens
   * the drawer, so nothing about single-shift work changed; SHIFT+CLICK picks
   * one day and a DRAG paints a rectangle. Both are additive to whatever is
   * already picked, and both reverse when they start on a day already picked. */
  const [selected, setSelected] = useState(() => new Set())

  /* Drag paints a RECTANGLE — people down, days across — because the thing a
   * manager reaches for is "these three assistants, that fortnight", and doing
   * it a row at a time is the tedium this mode exists to remove.
   *
   * The cell you START on decides which way the drag goes: begin on an unpicked
   * day and the rectangle picks, begin on a picked one and it un-picks. Same
   * gesture both ways, no modifier to remember — and it matches what a single
   * click on that cell would have done, so the rule is learnable from one try. */
  const flatPeople = useMemo(() => groups.flatMap((g) => g.people), [groups])
  const rowIndex = useMemo(() => new Map(flatPeople.map((p, i) => [p.id, i])), [flatPeople])
  const [drag, setDrag] = useState(null)      // { r0, c0, r1, c1 } while the button is down
  const dragRef = useRef(null)
  dragRef.current = drag

  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people])
  const openIdx = useMemo(
    () => flatPeople.findIndex((p) => p.id === Number(openPersonId)),
    [flatPeople, openPersonId],
  )
  const openPerson = openIdx >= 0 ? flatPeople[openIdx] : null
  const selectedList = useMemo(() => (
    [...selected].map((k) => {
      const [id, date] = k.split('|')
      return { employeeId: Number(id), date }
    })
  ), [selected])
  const selectedPeople = useMemo(() => {
    const ids = new Set(selectedList.map((c) => c.employeeId))
    return [...ids].map((id) => peopleById.get(id)).filter(Boolean)
  }, [selectedList, peopleById])

  useEffect(() => {
    function onUp() {
      const d = dragRef.current
      if (!d) return
      dragRef.current = null
      setDrag(null)
      // No movement → this was a plain click; the cell's own onClick toggles it
      // (and honours Shift). Committing here too would immediately undo that.
      if (d.r0 === d.r1 && d.c0 === d.c1) return
      const [r0, r1] = d.r0 <= d.r1 ? [d.r0, d.r1] : [d.r1, d.r0]
      const [c0, c1] = d.c0 <= d.c1 ? [d.c0, d.c1] : [d.c1, d.c0]
      const removing = d.mode === 'remove'
      setSelected((prev) => {
        const next = new Set(prev)
        for (let r = r0; r <= r1; r++) {
          const p = flatPeople[r]
          if (!p) continue
          for (let c = c0; c <= c1; c++) {
            const date = days[c]?.date
            if (!date) continue
            const k = cellKey(p.id, date)
            // Un-picking needs no eligibility test — anything picked was
            // eligible when it went in, and a day that has since been taken
            // should come OUT of the selection, not be stuck in it.
            if (removing) next.delete(k)
            else if (selectable(p, date)) next.add(k)
          }
        }
        return next
      })
    }
    window.addEventListener('mouseup', onUp)
    return () => window.removeEventListener('mouseup', onUp)
  }, [flatPeople, days, foreign, branchId]) // eslint-disable-line react-hooks/exhaustive-deps

  const inDrag = (r, c) => {
    if (!drag) return false
    const [r0, r1] = drag.r0 <= drag.r1 ? [drag.r0, drag.r1] : [drag.r1, drag.r0]
    const [c0, c1] = drag.c0 <= drag.c1 ? [drag.c0, drag.c1] : [drag.c1, drag.c0]
    return r >= r0 && r <= r1 && c >= c0 && c <= c1
  }

  /* Picking someone is the moment their other branches start to matter, so the
   * lookup is fetched for exactly those people — not the whole roster, and not
   * only when the toggle happens to be on. Cached, so re-picking is free. */
  const selectedIdsKey = useMemo(
    () => [...new Set(selectedList.map((c) => c.employeeId))].sort((a, b) => a - b).join(','),
    [selectedList],
  )
  useEffect(() => {
    if (!selectedIdsKey) return
    onNeedForeign?.(selectedIdsKey.split(',').map(Number))
  }, [selectedIdsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Days in the selection this person is ALREADY working somewhere else. Xano
   * refuses one row per (employee, date) clinic-wide, so these would each come
   * back as a failure after the fact — named here instead, before the assign. */
  const clashes = useMemo(() => {
    const out = []
    for (const c of selectedList) {
      const other = foreignRowOn(foreign?.get(c.employeeId), c.date, branchId)
      if (other) out.push({ ...c, row: other, person: peopleById.get(c.employeeId) })
    }
    return out
  }, [selectedList, foreign, branchId, peopleById])
  const clashKeys = useMemo(() => new Set(clashes.map((c) => cellKey(c.employeeId, c.date))), [clashes])

  function clearSelection() { setSelected(new Set()) }

  function toggleCell(person, date) {
    setSelected((prev) => {
      const next = new Set(prev)
      const k = cellKey(person.id, date)
      if (next.has(k)) next.delete(k)
      else if (selectable(person, date)) next.add(k)
      return next
    })
  }

  // Only days with nothing on them are selectable: this action creates rows, and
  // Xano allows one per (employee, date) anywhere in the clinic.
  //
  // The cross-branch answer is honoured WHENEVER we hold it — the toggle decides
  // whether we go and fetch it for everyone, not whether a day we already know
  // is taken counts as free.
  function selectable(person, date) {
    if (person.byDate.get(date)) return false
    const others = foreign?.get(person.id)
    return !(others && foreignRowOn(others, date, branchId))
  }

  if (!people.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-14 text-center text-[13.5px] text-slate-400">
        ยังไม่มีพนักงานประจำสาขานี้ และยังไม่มีเวรในเดือนนี้
      </div>
    )
  }

  const stickyName = `${NAME_COL} sticky left-0 z-10`

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
        <Users size={14} className="text-slate-400" />
        <span className="text-[12.5px] text-slate-500">
          {people.length} คน · {monthTotal.toLocaleString()} เวรในเดือนนี้
        </span>

        {/* Loading, not a switch: every person's other branches are always
            fetched now, because the count beside their name is a whole-clinic
            number and a day they are working elsewhere is not a day they are
            free. This only says the answer is still on its way. */}
        {foreignLoading && (
          <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-400">
            <Loader2 size={12} className="animate-spin" /> กำลังตรวจเวรสาขาอื่น…
          </span>
        )}

        {selected.size === 0 && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-slate-400">
            <MousePointerSquareDashed size={12} />
            เลือกหลายวัน: Shift + คลิก หรือ ลากคลุม
          </span>
        )}
      </div>

      <div className="max-h-[70vh] overflow-auto">
        <table className="border-separate border-spacing-0 text-[12px] w-full select-none">
          <thead>
            <tr>
              <th style={{ top: 0, height: H_DATES }}
                className={`${NAME_COL} sticky left-0 z-30 bg-slate-50 px-3 text-left text-[11.5px] font-medium text-slate-400 border-b border-r border-slate-200`}>
                พนักงาน
              </th>
              {days.map((d) => (
                <th key={d.date} style={{ top: 0, height: H_DATES }}
                  className={`${COL} sticky z-20 px-0 text-center border-b border-slate-200 leading-tight ${weekEdge(d)}
                    ${d.weekend ? 'bg-slate-100' : 'bg-slate-50'}
                    ${d.date === today ? '!bg-brand-100' : ''}`}>
                  <div className="text-[10px] text-slate-400">{DOW_TH[d.dow]}</div>
                  <div className={`text-[11.5px] tabular-nums ${d.date === today ? 'font-bold text-brand-700' : 'text-slate-500'}`}>{d.day}</div>
                </th>
              ))}
            </tr>

            {/* Pinned under the dates: how many doctors that day. Context for the
                headcount, never a rule — this clinic's assistant-to-doctor ratio
                runs anywhere from 5:3 to 2:4 depending on the branch. */}
            <tr>
              <th style={{ top: H_DATES, height: H_DOCTORS }}
                className={`${NAME_COL} sticky left-0 z-30 bg-white px-3 text-left text-[11.5px] font-medium text-brand-700 border-b border-r border-slate-200`}>
                <span className="inline-flex items-center gap-1.5"><Stethoscope size={12} /> หมอออกตรวจ</span>
              </th>
              {days.map((d) => {
                const n = doctorsByDay?.get(d.date) || 0
                return (
                  <td key={d.date} style={{ top: H_DATES, height: H_DOCTORS }}
                    className={`${COL} sticky z-20 text-center tabular-nums border-b border-slate-200 text-[11.5px] ${weekEdge(d)}
                      ${d.weekend ? 'bg-slate-50' : 'bg-white'} ${n ? 'text-brand-700 font-medium' : 'text-slate-200'}`}>
                    {n || '·'}
                  </td>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {groups.map((g) => (
              <Group key={g.position || '—'} group={g} days={days} byDay={byDay} clashKeys={clashKeys}
                onOpenPerson={onOpenPerson}
                branchId={branchId} branchName={branchName} foreign={foreign}
                today={today} selected={selected} stickyName={stickyName}
                onCreate={onCreate} onEdit={onEdit} onToggle={toggleCell} selectable={selectable}
                rowIndex={rowIndex} inDrag={inDrag} dragMode={drag?.mode}
                onCellDown={(person, c, e) => {
                  e?.preventDefault?.()
                  const r = rowIndex.get(person.id)
                  const mode = selected.has(cellKey(person.id, days[c].date)) ? 'remove' : 'add'
                  setDrag({ r0: r, c0: c, r1: r, c1: c, mode })
                }}
                onCellEnter={(person, c) => setDrag((d) => (d ? { ...d, r1: rowIndex.get(person.id), c1: c } : d))} />
            ))}
          </tbody>

          <tfoot>
            <tr>
              <th style={{ bottom: 0 }}
                className={`${NAME_COL} sticky left-0 z-30 bg-slate-100 px-3 py-1.5 text-left text-[11.5px] font-semibold text-slate-600 border-t border-r border-slate-200`}>
                รวมคน/วัน
              </th>
              {days.map((d) => {
                const cell = byDay.get(d.date)
                return (
                  <td key={d.date} style={{ bottom: 0 }}
                    className={`${COL} sticky z-20 text-center tabular-nums border-t border-slate-200 py-1 text-[12px] font-semibold bg-slate-100 ${weekEdge(d)}
                      ${cell?.total ? 'text-slate-800' : 'text-slate-300'}`}
                    title={cell?.leave ? `ลา ${cell.leave} คน (ไม่นับเป็นกำลังคน)` : undefined}>
                    {cell?.total || '—'}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {openPerson && (
        <EmployeeMonthDrawer
          key={openPerson.id}
          person={openPerson}
          // The cross-branch month when we have it, this branch's rows until we
          // do — the drawer's whole point is the days spent somewhere else.
          rows={foreign?.get(openPerson.id) || openPerson.rows}
          ym={ym}
          branchId={branchId}
          branches={branches}
          onClose={() => onOpenPerson?.(null)}
          onPrev={() => onOpenPerson?.(flatPeople[(openIdx - 1 + flatPeople.length) % flatPeople.length]?.id)}
          onNext={() => onOpenPerson?.(flatPeople[(openIdx + 1) % flatPeople.length]?.id)}
          // The person travels with the edit, so the drawer that opens next
          // knows where it was opened FROM and can offer the way back.
          onEdit={(row) => onEdit?.(row, openPerson.id)}
          onCreate={(date, id, type) => onCreate?.(date, id, type, openPerson.id)}
        />
      )}

      {selected.size > 0 ? (
        <SelectionBar
          count={selected.size}
          people={selectedPeople}
          clashes={clashes}
          branchName={branchName}
          busy={busy}
          onClear={clearSelection}
          onAssign={(type) => onBulkAssign?.(
            selectedList.filter((c) => !clashKeys.has(cellKey(c.employeeId, c.date))),
            type,
            () => clearSelection(),
          )}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 border-t border-slate-100 text-[11px] text-slate-400">
          <span>ผช = เวรผู้ช่วย</span><span>ช = กะเช้า</span><span>บ = กะบ่าย</span>
          <span>ค = กะควบ</span><span>Off/W = Office/WFH</span>
          <span className="text-amber-600">กิจ</span><span className="text-rose-600">ป่วย</span>
          <span>= วันลา (ไม่นับเป็นกำลังคน)</span>
          <span>→ = เข้าเวรสาขาอื่น</span>
          <span className="inline-flex items-center gap-1">
            <span className="h-[4px] w-[4px] rounded-full bg-slate-500" /> = อนุญาต OT
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="text-[9px] leading-none text-slate-500">★</span> = OT พิเศษ
          </span>
          <span>ก/ป/LWP/OT/OT★ หลังชื่อ = จำนวนลากิจ · ลาป่วย · Leave without pay · อนุญาต OT · OT พิเศษ</span>
        </div>
      )}
    </div>
  )
}

/* ── the action bar for a selection ──────────────────────────────────────
 * เวรผู้ช่วย is disabled the moment the selection includes someone without the
 * Assistance role, and the people blocking it are NAMED — a greyed chip with no
 * reason is a puzzle, and the fix (deselect those two) is not guessable. */
function SelectionBar({ count, people, clashes = [], branchName, busy, onClear, onAssign }) {
  const ok = count - clashes.length
  return (
    <div className="border-t border-brand-100 bg-brand-50/70 px-4 py-2.5">
      {/* Named before the assign, not reported after it: each of these would
          come back from Xano as its own failure, and a list of failures is a
          worse way to learn something you could have been told up front. */}
      {clashes.length > 0 && (
        <div className="mb-2 rounded-xl bg-rose-50 ring-1 ring-rose-200 px-3 py-2 text-[12px] text-rose-800">
          <div className="font-medium">
            {clashes.length} วันที่เลือกไว้ ติดเวรที่สาขาอื่นอยู่แล้ว — จะไม่ถูกเพิ่ม
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {clashes.slice(0, 3).map((c) => (
              <li key={`${c.employeeId}|${c.date}`} className="tabular-nums">
                {c.person?.code || `#${c.employeeId}`} · {c.date} — {c.row.type} ที่{' '}
                {relabelBranch(branchName?.get(Number(c.row.branch_id)) || '') || 'สาขาอื่น'}
              </li>
            ))}
            {clashes.length > 3 && <li>และอีก {clashes.length - 3} รายการ</li>}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] font-semibold text-brand-800 tabular-nums">
          เลือก {count} วัน{people.length > 1 ? ` · ${people.length} คน` : ''}
          {clashes.length > 0 && <span className="font-normal text-rose-700"> · เพิ่มได้ {ok}</span>}
        </span>
        <span className="text-[12px] text-slate-500">กำหนดเป็น</span>
        {TIMETABLE_TYPES.map((t) => {
              const blocked = ineligibleFor(t, people)
          const disabled = busy || blocked.length > 0 || ok < 1
          return (
            <button key={t} type="button" disabled={disabled}
              onClick={() => onAssign(t)}
              title={blocked.length
                ? `${blocked.slice(0, 3).map((p) => p.code).join(', ')}${blocked.length > 3 ? ` และอีก ${blocked.length - 3} คน` : ''} ไม่มีสิทธิ์ Assistance`
                : `กำหนด ${t} ให้ ${count} วันที่เลือก`}
              className={`rounded-full px-3 py-1 text-[12px] font-medium ring-1 transition ${
                disabled
                  ? 'bg-slate-50 text-slate-300 ring-slate-200 cursor-not-allowed'
                  : `${shiftTypeTone(t)} hover:brightness-95`}`}>
              {t}
            </button>
          )
        })}
        <button onClick={onClear} disabled={busy}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[12px] text-slate-500 hover:bg-white disabled:opacity-50">
          <X size={12} /> ล้างที่เลือก
        </button>
      </div>
    </div>
  )
}

function Group({
  group, days, byDay, clashKeys, branchId, branchName, foreign, today,
  selected, stickyName, onCreate, onEdit, onToggle, selectable,
  rowIndex, inDrag, dragMode, onCellDown, onCellEnter, onOpenPerson,
}) {
  return (
    <>
      {/* The band's own per-day headcount, on the band itself — the position
          totals belong where those people are, not in a footer you have to
          match back up by column. */}
      <tr className="bg-slate-50/90">
        <th className={`${stickyName} bg-slate-100 px-3 py-1 text-left text-[11.5px] font-semibold text-slate-600 border-y border-slate-200`}>
          {group.label} · {group.people.length} คน
        </th>
        {days.map((d) => {
          const n = byDay.get(d.date)?.byPosition.get(group.position) || 0
          return (
            <td key={d.date}
              className={`w-[34px] min-w-[34px] text-center tabular-nums py-1 text-[11.5px] font-medium border-y border-slate-200 bg-slate-50 ${weekEdge(d)}
                ${n ? 'text-slate-700' : 'text-slate-300'}`}>
              {n || '·'}
            </td>
          )
        })}
      </tr>
      {group.people.map((p) => (
        <PersonRow key={p.id} person={p} days={days} clashKeys={clashKeys} branchId={branchId}
          branchName={branchName} foreign={foreign} today={today}
          selected={selected} stickyName={stickyName}
          onCreate={onCreate} onEdit={onEdit} onToggle={onToggle} selectable={selectable}
          rowIndex={rowIndex} inDrag={inDrag} dragMode={dragMode}
          onCellDown={onCellDown} onCellEnter={onCellEnter}
          onOpenPerson={() => onOpenPerson?.(p.id)} />
      ))}
    </>
  )
}

function PersonRow({
  person, days, clashKeys, branchId, branchName, foreign, today,
  selected, stickyName, onCreate, onEdit, onToggle, selectable,
  rowIndex, inDrag, dragMode, onCellDown, onCellEnter, onOpenPerson,
}) {
  const type = predictType(person)
  const others = foreign?.get(person.id) || null
  /* The cross-branch lookup returns this person's WHOLE month, every branch —
   * which is the honest denominator for "how many shifts do they carry". Falls
   * back to what this branch holds until it lands, so the number never blanks. */
  const stats = personStats({ rows: others || person.rows })
  const hereCount = useMemo(
    () => (others ? person.rows.filter((r) => !isLeaveRow(r)).length : null),
    [others, person.rows],
  )

  return (
    <tr className="group/row">
      {/* Every background on this cell must be FULLY opaque: it is sticky, so
          the day columns scrolled off to the left sit directly underneath it,
          and a translucent tint lets their chips show through the names. */}
      <th className={`${stickyName} bg-white group-hover/row:bg-brand-50 px-3 py-1 text-left border-b border-r border-slate-100`}>
        <div className="flex items-baseline gap-1.5 min-w-0">
          <button type="button" onClick={onOpenPerson}
            title={`ดูเวรทั้งเดือนของ ${person.code}`}
            className="truncate text-left text-[12px] font-medium text-slate-700 hover:text-brand-700 hover:underline decoration-dotted underline-offset-2">
            {person.code}
          </button>
          {!person.home && (
            <span className="shrink-0 text-[10px] text-amber-600" title={`ประจำสาขา ${relabelBranch(branchName.get(Number(person.employee?.branch_id)) || '') || '—'}`}>
              ย้ายสาขา
            </span>
          )}
          {/* The shift count sits LAST and on a fixed width, so it lines up as a
              column down the roster however many badges precede it — comparing
              those numbers person to person is the whole reason they are here,
              and a number that moves left and right with the badges cannot be
              compared at a glance. */}
          <span className="ml-auto flex items-baseline gap-1.5 shrink-0">
            <Flags stats={stats} />
            <span className="w-[22px] text-right tabular-nums text-[11.5px] font-semibold text-slate-600"
              title={hereCount != null && hereCount !== stats.shifts
                ? `เดือนนี้ทั้งหมด ${stats.shifts} เวร (สาขานี้ ${hereCount})`
                : `เดือนนี้ ${stats.shifts} เวร`}>
              {stats.shifts}
            </span>
          </span>
        </div>
      </th>

      {days.map((d, c) => (
        <Cell key={d.date} day={d} person={person} type={type} branchId={branchId}
          branchName={branchName} others={others} today={today}
          selected={selected.has(`${person.id}|${d.date}`)}
          clash={clashKeys?.has(`${person.id}|${d.date}`)}
          canSelect={selectable(person, d.date)}
          dragging={inDrag(rowIndex.get(person.id), c)} dragMode={dragMode}
          onDown={(e) => onCellDown(person, c, e)}
          onEnter={() => onCellEnter(person, c)}
          onCreate={onCreate} onEdit={onEdit} onToggle={onToggle} />
      ))}
    </tr>
  )
}

function Cell({
  day, person, type, branchId, branchName, others, today,
  selected, clash, canSelect, dragging, dragMode, onDown, onEnter, onCreate, onEdit, onToggle,
}) {
  const row = person.byDate.get(day.date)
  const base = `${COL} p-0 text-center border-b border-slate-100 ${weekEdge(day)} ${day.weekend ? 'bg-slate-50/60' : ''} ${day.date === today ? 'bg-brand-50/40' : ''}`

  if (row) {
    // Same tones as the calendar chips and the ลงเวลา report — a กะเช้า must be
    // the same colour wherever this clinic looks at it.
    return (
      <td className={base} onMouseEnter={onEnter} onMouseDown={onDown}>
        <button onClick={() => onEdit?.(row)}
          title={[
            `${person.code} · ${row.type}`,
            isLeaveRow(row) ? '' : relabelBranch(branchName.get(Number(row.branch_id)) || '') || '—',
            row.allow_OT === true ? 'อนุญาต OT' : '',
            row.special_OT === true ? 'OT พิเศษ' : '',
          ].filter(Boolean).join(' · ')}
          className={`relative w-full h-[24px] leading-[24px] text-[10.5px] font-medium hover:brightness-95 ${shiftTypeTone(row.type)}`}>
          {shortType(row.type)}
          {/* Corner pips rather than a letter: the chip has room for two glyphs
              and the type has already claimed both.
              `bg-current` — the pip inherits the chip's own text colour, so it
              reads as part of that shift (teal on a เวรผู้ช่วย, sky on a กะเช้า)
              instead of introducing a colour the grid does not otherwise use.
              Which flag it is comes from the SHAPE: a round pip for OT, a star
              for OT พิเศษ. Opposite corners as well, so a chip carrying both is
              two separate marks rather than one crowded one. */}
          {row.allow_OT === true && (
            <span className="absolute top-[2px] right-[2px] h-[4px] w-[4px] rounded-full bg-current" />
          )}
          {row.special_OT === true && (
            <span className="absolute bottom-0 right-[1px] text-[8px] leading-[8px]">★</span>
          )}
        </button>
      </td>
    )
  }

  // No row HERE. A null `others` means the cross-branch lookup is off or has not
  // run for this person, so "free" and "working elsewhere" are indistinguishable
  // — the cell stays blank and clickable, and the drawer re-checks before saving.
  const elsewhere = others ? foreignRowOn(others, day.date, branchId) : null
  if (elsewhere) {
    const where = relabelBranch(branchName.get(Number(elsewhere.branch_id)) || '') || 'สาขาอื่น'
    return (
      <td className={base} onMouseEnter={onEnter}>
        <div className="w-full h-[24px] leading-[24px] text-[11px] text-slate-400 bg-slate-50 cursor-not-allowed"
          title={`${person.code} เข้าเวร ${elsewhere.type} ที่ ${where} วันนี้ — แก้ไขได้ที่ตารางเวรของสาขานั้น`}>
          →
        </div>
      </td>
    )
  }

  // What releasing right now would do to THIS cell — shown before it happens,
  // because a rectangle you cannot see the effect of is one you have to undo to
  // understand.
  const losing = dragging && dragMode === 'remove' && selected
  const gaining = dragging && dragMode === 'add' && canSelect && !selected

  return (
    // The drag listeners live on the CELL, not the button, so a rectangle keeps
    // tracking across days it cannot pick instead of stopping at the first one.
    <td className={base} onMouseEnter={onEnter} onMouseDown={onDown}>
      <button type="button"
        // Shift picks, a bare click opens the drawer. Split here rather than in
        // a mode, so the ordinary one-shift click never changes meaning.
        onClick={(e) => (e.shiftKey ? onToggle(person, day.date) : onCreate?.(day.date, person.id, type))}
        title={selected ? `${person.code} · ${day.day} (เลือกไว้)` : `เพิ่ม ${type} ให้ ${person.code}`}
        className={`w-full h-[24px] leading-[24px] text-[11px] transition ${
          losing
            ? 'bg-rose-100 text-rose-500'
            : clash
              ? 'bg-rose-500 text-white font-semibold'
              : selected
                ? 'bg-brand-500 text-white font-semibold'
                : gaining
                  ? 'bg-brand-200 text-brand-800'
                  : 'text-slate-200 hover:bg-brand-100 hover:text-brand-700'}`}>
        {losing ? '✕' : clash ? '!' : selected ? '✓' : '·'}
      </button>
    </td>
  )
}
